/**
 * Componente ParallaxImage
 * Imagen con efecto parallax suave al hacer scroll
 * Crea profundidad y dinamismo visual tipo Heinz/Apple
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Imagen con efecto parallax
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.src - URL de la imagen
 * @param {string} props.alt - Texto alternativo
 * @param {number} props.speed - Velocidad del parallax (-1 a 1, negativo = dirección opuesta)
 * @param {string} props.className - Clases CSS del contenedor
 * @param {string} props.imgClassName - Clases CSS de la imagen
 * @param {boolean} props.scale - Si la imagen debe escalar al hacer scroll
 * @param {boolean} props.overlay - Si debe mostrar un overlay oscuro
 * @param {string} props.overlayColor - Color del overlay
 * @param {number} props.overlayOpacity - Opacidad del overlay (0-1)
 * @returns {JSX.Element} Imagen con parallax
 */
const ParallaxImage = ({
  src,
  alt = '',
  speed = 0.3,
  className = '',
  imgClassName = '',
  scale = false,
  overlay = false,
  overlayColor = '#000000',
  overlayOpacity = 0.3,
}) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const image = imageRef.current;

    if (!container || !image) return;

    // Configurar el parallax con GSAP
    const yMovement = speed * 100; // Convertir velocidad a porcentaje

    gsap.to(image, {
      yPercent: yMovement,
      ease: 'none',
      scrollTrigger: {
        trigger: container,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    });

    // Efecto de escala opcional
    if (scale) {
      gsap.fromTo(image,
        { scale: 1.2 },
        {
          scale: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: container,
            start: 'top bottom',
            end: 'center center',
            scrub: true,
          },
        }
      );
    }

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === container) {
          trigger.kill();
        }
      });
    };
  }, [speed, scale]);

  return (
    <div 
      ref={containerRef} 
      className={`relative overflow-hidden ${className}`}
    >
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        className={`w-full h-[160%] object-cover will-change-transform ${imgClassName}`}
        style={{ 
          marginTop: '-40%',
          transform: 'translateZ(0)', // Forzar aceleración por hardware
        }}
      />
      
      {/* Overlay opcional */}
      {overlay && (
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{ 
            backgroundColor: overlayColor,
            opacity: overlayOpacity,
          }}
        />
      )}
    </div>
  );
};

ParallaxImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  speed: PropTypes.number,
  className: PropTypes.string,
  imgClassName: PropTypes.string,
  scale: PropTypes.bool,
  overlay: PropTypes.bool,
  overlayColor: PropTypes.string,
  overlayOpacity: PropTypes.number,
};

export default ParallaxImage;
